import { BaseService } from './BaseService'

/**
 * Brand curation: merging name variants of the same brand, renaming the label people see,
 * and the journal that makes both reversible.
 *
 * A merge is non-destructive — it sets a pointer on the losing brand row, never rewrites
 * detections — so every merge undoes with one call and chains (a -> b -> c) are ordinary.
 */

export interface BrandMember {
  id: string
  brand_name: string          // the scan pipeline's match key — never edited
  display_name: string | null // the curated label, null = use brand_name
  label: string               // COALESCE(display_name, brand_name)
  category: string | null
  is_canonical: boolean
  depth: number
  merged_at: string | null
  merged_by: string | null
  first_seen_at: string | null
  last_seen_at: string | null
  detections: number
  posts: number
  /** A post THIS spelling matched, so a reviewer can see what the alias actually caught. */
  post_url: string | null
  post_id: number | null
}

export interface CurationBrand {
  id: string
  brand_name: string
  display_name: string | null
  label: string
  category: string | null
  heat_score: number
  last_seen_at: string | null
  /** How many aliases are merged into this brand. 0 = a plain brand. */
  /** True when the customer has marked this as their own brand: still detected and listed,
   *  but kept out of rollup totals unless explicitly included. */
  is_self: boolean
  member_count: number
  /** A post this brand was detected in — highest-confidence first, so it is the clearest
   *  example rather than an incidental background sighting. Null when none is recorded. */
  post_url: string | null
  posts: number
  /** Post-level EMV across the whole chain, deduped: a post carrying two merged variants
   *  counts once. */
  total_emv: number
  detections: number
}

export interface SelfCandidate {
  id: string
  label: string
  is_self: boolean
  posts: number
  customer_name: string
  /** exact = name matches the customer; own_account_handle = matches one of their accounts;
   *  name_prefix = starts with the customer name (the family case — needs judgement). */
  match_kind: 'exact' | 'own_account_handle' | 'name_prefix'
}

export interface MergePreview {
  target_posts: number
  source_posts: number
  /** Posts carrying BOTH brands. They are counted once after the merge, which is why the
   *  combined totals are less than the two added together. */
  shared_posts: number
  combined_posts: number
  target_emv: number
  source_emv: number
  combined_emv: number
  overlapNote: string | null
}

export interface MergeLogEntry {
  id: string
  action: 'merge' | 'unmerge'
  source_brand_id: string
  target_brand_id: string | null
  source_name: string
  target_name: string | null
  origin: 'auto' | 'support' | 'customer'
  rule: string | null
  confidence: number | null
  actor_user_id: string | null
  note: string | null
  created_at: string
  /** Whether the merge this row describes is still in force — drives the Undo affordance. */
  still_merged: boolean
}

export interface BrandSuggestion {
  id: string
  /** 2 = structural patterns, usually right. 3 = containment and initials: review carefully. */
  tier: 2 | 3
  rule: string
  score: number
  reason: string
  status: 'pending' | 'accepted' | 'rejected'
  created_at: string
  source_brand_id: string
  target_brand_id: string
  source_label: string
  target_label: string
  source_posts: number
  target_posts: number
  source_accounts: string[]
  target_accounts: string[]
  /** Two genuinely different brands rarely appear on the same accounts. */
  shared_accounts: number
  source_post_url: string | null
  target_post_url: string | null
}

interface SuggestionsResponse {
  success: boolean
  data: BrandSuggestion[]
  /** regenerated = the queue was brought up to date with a scan that landed since it was
   *  last built, so the caller can say so rather than silently showing different rows. */
  meta?: { regenerated: boolean }
  pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number }
}
interface GenerateResponse {
  success: boolean
  data: { scanned: number; proposed: number; skipped: number }
  message?: string
}
interface BulkResponse {
  success: boolean
  data: { merged?: number; skipped?: number; rejected?: number }
  message?: string
}

interface CurationListResponse {
  success: boolean
  data: CurationBrand[]
  pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number }
}
interface MembersResponse { success: boolean; data: { canonicalId: string; members: BrandMember[] } }
interface PreviewResponse { success: boolean; data: MergePreview }
interface MergeResponse {
  success: boolean
  data: { canonicalId: string; merged?: Array<{ id: string; label: string }>; unmerged?: Array<{ id: string; label: string }> }
  message?: string
}
interface DisplayNameResponse {
  success: boolean
  data: { id: string; brand_name: string; display_name: string | null; label: string }
}
interface MergeLogResponse {
  success: boolean
  data: MergeLogEntry[]
  pagination: { currentPage: number; totalPages: number; totalItems: number; itemsPerPage: number }
}

class ScoutCurationService extends BaseService {
  constructor() { super('cro') }

  /** Canonical brands for the curation screen, unfiltered by account category. */
  async listBrands(
    customerId: string,
    opts: {
      search?: string
      sortBy?: 'posts' | 'emv' | 'detections' | 'label' | 'last_seen_at' | 'member_count'
      order?: 'asc' | 'desc'
      page?: number
      limit?: number
    } = {}
  ): Promise<CurationListResponse> {
    return this.get<CurationListResponse>('/scout/brands/curation-list', {
      customerId, search: opts.search, sortBy: opts.sortBy, order: opts.order,
      page: opts.page, limit: opts.limit
    })
  }

  /** The canonical brand and every alias merged into it, each with its own evidence. */
  async getMembers(brandId: string, customerId: string): Promise<MembersResponse> {
    return this.get<MembersResponse>(`/scout/brands/${brandId}/members`, { customerId })
  }

  /** What a merge would move, before it is confirmed. */
  async previewMerge(brandId: string, sourceBrandIds: string[], customerId: string): Promise<PreviewResponse> {
    return this.get<PreviewResponse>(`/scout/brands/${brandId}/merge-preview`, {
      customerId, sourceBrandIds: sourceBrandIds.join(',')
    })
  }

  async merge(brandId: string, sourceBrandIds: string[], customerId: string, note?: string): Promise<MergeResponse> {
    return this.post<MergeResponse>(`/scout/brands/${brandId}/merge`, { sourceBrandIds, note }, { customerId })
  }

  /** Per edge: releasing a -> b leaves any b -> c intact. */
  async unmerge(brandId: string, sourceBrandIds: string[], customerId: string): Promise<MergeResponse> {
    return this.post<MergeResponse>(`/scout/brands/${brandId}/unmerge`, { sourceBrandIds }, { customerId })
  }

  /** Renames the LABEL only. brand_name stays as the scan pipeline wrote it. */
  async setDisplayName(brandId: string, displayName: string | null, customerId: string): Promise<DisplayNameResponse> {
    return this.patch<DisplayNameResponse>(`/scout/brands/${brandId}/display-name`, { displayName }, { customerId })
  }

  async getSuggestions(
    customerId: string,
    opts: { status?: 'pending' | 'accepted' | 'rejected'; tier?: 2 | 3; page?: number; limit?: number } = {}
  ): Promise<SuggestionsResponse> {
    return this.get<SuggestionsResponse>('/scout/brands/suggestions', {
      customerId, status: opts.status, tier: opts.tier, page: opts.page, limit: opts.limit
    })
  }

  async generateSuggestions(customerId: string, dryRun = false): Promise<GenerateResponse> {
    return this.post<GenerateResponse>('/scout/brands/suggestions/generate', {}, { customerId, dryRun })
  }

  /** swap reverses the direction — frequently the only thing wrong with a suggestion. */
  async acceptSuggestion(id: string, customerId: string, swap = false): Promise<BulkResponse> {
    return this.post<BulkResponse>(`/scout/brands/suggestions/${id}/accept`, { swap }, { customerId })
  }

  async rejectSuggestion(id: string, customerId: string): Promise<BulkResponse> {
    return this.post<BulkResponse>(`/scout/brands/suggestions/${id}/reject`, {}, { customerId })
  }

  async bulkReview(
    customerId: string,
    body: { action: 'accept' | 'reject'; suggestionIds?: string[]; tier?: 2 | 3; rule?: string }
  ): Promise<BulkResponse> {
    return this.post<BulkResponse>('/scout/brands/suggestions/bulk', body, { customerId })
  }

  /** Brands that look like the customer's own. Proposed for review, never auto-applied. */
  async getSelfCandidates(customerId: string): Promise<{ success: boolean; data: SelfCandidate[] }> {
    return this.get<{ success: boolean; data: SelfCandidate[] }>('/scout/brands/self-candidates', { customerId })
  }

  /** Mark a brand as the customer's own (out of rollup totals) or clear it. */
  async setIsSelf(brandId: string, isSelf: boolean, customerId: string): Promise<{ success: boolean; message?: string }> {
    return this.patch<{ success: boolean; message?: string }>(`/scout/brands/${brandId}/self`, { isSelf }, { customerId })
  }

  async getMergeLog(
    customerId: string,
    opts: { origin?: 'auto' | 'support' | 'customer'; page?: number; limit?: number } = {}
  ): Promise<MergeLogResponse> {
    return this.get<MergeLogResponse>('/scout/brands/merge-log', {
      customerId, origin: opts.origin, page: opts.page, limit: opts.limit
    })
  }
}

export const scoutCurationService = new ScoutCurationService()
export default scoutCurationService
